import { acquireContext, type ContextInfo } from "./gl/context";
import { RafLoop } from "./loop";
import {
  hexToLinear,
  PALETTES,
  paletteStopsLinear,
  type PaletteId,
} from "./palettes";
import { DEFAULTS, REINIT_KEYS, sanitizeParam } from "./params";
import { deriveStreams, MAX_OCTAVES, newSeedString } from "./prng";
import {
  computeMaxExportScale,
  exportPng,
  type ExportHost,
} from "./render/exporter";
import {
  createRenderPrograms,
  destroyRenderPrograms,
  Renderer,
  type RenderPrograms,
  type Rgb,
  type StepUniforms,
} from "./render/renderer";
import {
  createSimProgram,
  plotTotalSteps,
  Simulation,
  type SimUniforms,
} from "./sim/simulation";
import { buildInitialState } from "./sim/state";
import {
  EngineError,
  type Engine,
  type EngineEvents,
  type EngineOptions,
  type ExportScale,
  type ParamKey,
  type Params,
} from "./types";
import type { LinkedProgram } from "./gl/shader";

export { EngineError };
export type {
  Engine,
  EngineEvents,
  EngineOptions,
  ExportScale,
  ParamKey,
  Params,
};

/** Wind velocity at strength 1, domain units per second. */
const WIND_SPEED = 0.15;
const MAX_DPR = 2;
/** Auto-tune: frames 31..150 averaged; above this we halve dpr toward 1. */
const TUNE_WARMUP_FRAMES = 30;
const TUNE_DECIDE_FRAME = 150;
const TUNE_FRAME_MS = 20;
const FPS_EMIT_FRAMES = 60;

export function createEngine(
  canvas: HTMLCanvasElement,
  opts?: EngineOptions,
): Engine {
  return new FlowEngine(canvas, opts);
}

class FlowEngine implements Engine {
  private readonly canvas: HTMLCanvasElement;
  /** Debug (ignoreHidden) mode: throttled-timer frame gaps would poison
   *  fps averages, so auto-tune is disabled entirely. */
  private readonly debugLoop: boolean;
  private readonly ctx: ContextInfo;
  private readonly loop: RafLoop;
  private params: Params;
  private seedValue: string;

  private simProgram!: LinkedProgram;
  private programs!: RenderPrograms;
  private sim!: Simulation;
  private renderer!: Renderer;
  private octaveOffsets = new Float32Array(MAX_OCTAVES * 2);

  private cssW: number;
  private cssH: number;
  private dpr: number;

  private userRunning = true;
  /** True once the plot reached totalSteps — the loop is off and the last
   *  presented frame holds. Cleared by any restart (restartDrawing). */
  private settledFlag = false;
  private contextLost = false;
  private destroyed = false;
  private exportChain: Promise<unknown> = Promise.resolve();
  private exporting = false;
  /** Reinit-class work queued while an export replay is in flight. */
  private pendingReinit = false;
  private pendingReseed = false;
  private pendingPresent = false;
  /** Bumped on every context loss so an in-flight export can detect a
   *  lost-and-restored bounce (isContextLost() alone heals too fast). */
  private lossEpoch = 0;

  private frames = 0;
  private fpsAccumMs = 0;
  private fpsFrames = 0;
  private tuneAccumMs = 0;
  private tuned = false;
  /** Auto-tune's ceiling — resize() must never undo a measured downgrade. */
  private dprCap = MAX_DPR;

  private stopsCache: {
    id: PaletteId;
    stops: Float32Array;
    count: number;
  } | null = null;

  // Per-frame uniform objects, mutated in place — the render loop must not
  // allocate. The exporter receives copies (see runExport).
  private readonly simU: SimUniforms = {
    speed: 0,
    wind: [0, 0],
    vortex: 0,
    attract: 0,
    aspect: 1,
    flowScale: 0,
    octaves: 1,
    roughness: 0,
    flowStrength: 0,
    evolution: 0,
    octaveOffsets: this.octaveOffsets,
  };
  private readonly stepU: StepUniforms = {
    bg: [0, 0, 0],
    aspect: 1,
    stops: new Float32Array(0),
    stopCount: 0,
    dotSize: 0,
    resScale: 1,
    zoom: 1,
    sizeJitter: 0,
    dotOpacity: 0,
    glow: 0,
    symmetry: 0,
  };
  private readonly bgCache: Rgb = [0, 0, 0];
  private bgPalette: PaletteId | null = null;
  private bgShade = -1;

  private readonly listeners = new Map<keyof EngineEvents, Set<Function>>();

  constructor(canvas: HTMLCanvasElement, opts?: EngineOptions) {
    this.canvas = canvas;
    this.debugLoop = opts?.ignoreHidden ?? false;
    this.params = { ...DEFAULTS };
    if (opts?.params) this.mergePatch(opts.params);
    this.seedValue = opts?.seed ?? newSeedString();

    this.ctx = acquireContext(canvas, {
      onLost: () => this.handleContextLost(),
      onRestored: () => this.handleContextRestored(),
    });

    this.cssW = Math.max(1, canvas.clientWidth || canvas.width || 640);
    this.cssH = Math.max(1, canvas.clientHeight || canvas.height || 480);
    this.dpr = Math.min(globalThis.devicePixelRatio || 1, MAX_DPR);

    this.initGpu();
    this.glErrorSweep("init");

    this.loop = new RafLoop(
      (steps) => this.tick(steps),
      (frameMs) => this.onFrame(frameMs),
      opts?.ignoreHidden ?? false,
    );
    this.loop.setActive(true);
    // Present synchronously so a frame exists even if the caller pauses
    // before the first rAF fires (prefers-reduced-motion still frame).
    this.presentStill();
  }

  // ---- Engine interface -------------------------------------------------

  setParams(patch: Partial<Params>): void {
    const { changed, reinit } = this.mergePatch(patch);
    if (!changed || this.destroyed || this.contextLost) return;
    if (!reinit) {
      // Live keys are uniforms picked up next tick — but a paused or
      // settled engine has no tick, so the change must land as a still.
      this.presentStillIfIdle();
      return;
    }
    if (this.exporting) {
      // Rebuilding mid-replay would corrupt the export and its backup;
      // live keys applied above, reinit work runs after the export settles.
      this.pendingReinit = true;
      return;
    }
    this.restartDrawing();
  }

  getParams(): Readonly<Params> {
    return { ...this.params };
  }

  randomize(seed?: string): string {
    this.seedValue = seed ?? newSeedString();
    if (!this.destroyed && !this.contextLost) {
      // Last write wins: reseed() picks up this.seedValue when it runs.
      if (this.exporting) this.pendingReseed = true;
      else this.reseed();
    }
    return this.seedValue;
  }

  replay(): void {
    if (this.destroyed || this.contextLost) return;
    // A queued reinit after an export is exactly a restartDrawing().
    if (this.exporting) {
      this.pendingReinit = true;
      return;
    }
    this.restartDrawing();
  }

  get settled(): boolean {
    return this.settledFlag;
  }

  getSeed(): string {
    return this.seedValue;
  }

  pause(): void {
    this.userRunning = false;
    this.syncLoop();
  }

  resume(): void {
    this.userRunning = true;
    this.syncLoop();
  }

  get running(): boolean {
    return this.userRunning;
  }

  resize(cssWidth: number, cssHeight: number, dpr?: number): void {
    if (this.destroyed) return;
    this.cssW = Math.max(1, cssWidth);
    this.cssH = Math.max(1, cssHeight);
    this.dpr = Math.min(
      Math.max(dpr ?? globalThis.devicePixelRatio ?? this.dpr, 0.25),
      MAX_DPR,
      this.dprCap,
    );
    if (this.contextLost) return;
    this.applyCanvasSize();
  }

  maxExportScale(): ExportScale {
    return computeMaxExportScale(this.cssW, this.cssH, this.ctx.maxTextureSize);
  }

  exportPNG(scale: ExportScale, onProgress?: (t: number) => void): Promise<Blob> {
    const run = () => this.runExport(scale, onProgress);
    const next = this.exportChain.then(run, run);
    this.exportChain = next.catch(() => {});
    return next;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.loop.destroy();
    if (!this.contextLost) {
      this.sim.destroy();
      this.renderer.destroy();
      destroyRenderPrograms(this.ctx.gl, this.programs);
      this.ctx.gl.deleteProgram(this.simProgram.program);
    }
    this.ctx.dispose();
    this.listeners.clear();
  }

  on<K extends keyof EngineEvents>(
    ev: K,
    cb: (payload: EngineEvents[K]) => void,
  ): () => void {
    let set = this.listeners.get(ev);
    if (!set) {
      set = new Set();
      this.listeners.set(ev, set);
    }
    set.add(cb);
    return () => set.delete(cb);
  }

  // ---- Internals ---------------------------------------------------------

  private emit<K extends keyof EngineEvents>(ev: K, payload: EngineEvents[K]): void {
    const set = this.listeners.get(ev);
    if (!set) return;
    for (const cb of [...set]) (cb as (p: EngineEvents[K]) => void)(payload);
  }

  /** Sanitized merge; returns whether a reinit-class key changed. */
  private mergePatch(patch: Partial<Params>): {
    changed: boolean;
    reinit: boolean;
  } {
    let changed = false;
    let reinit = false;
    for (const key of Object.keys(patch) as ParamKey[]) {
      const next = sanitizeParam(key, patch[key]);
      if (next === undefined || this.params[key] === next) continue;
      this.assign(key, next);
      changed = true;
      if (REINIT_KEYS.has(key)) reinit = true;
    }
    return { changed, reinit };
  }

  private assign<K extends ParamKey>(key: K, value: Params[K]): void {
    this.params[key] = value;
  }

  private initGpu(): void {
    const gl = this.ctx.gl;
    this.simProgram = createSimProgram(gl);
    this.programs = createRenderPrograms(gl);
    this.applySeedStreams();

    this.canvas.width = Math.max(1, Math.round(this.cssW * this.dpr));
    this.canvas.height = Math.max(1, Math.round(this.cssH * this.dpr));

    this.renderer = new Renderer(
      gl,
      this.programs,
      this.ctx.floatTrails,
      this.ctx.maxPointSize,
    );
    this.renderer.setSize(this.canvas.width, this.canvas.height, this.bg());
    this.sim = new Simulation(gl, this.simProgram, this.buildState());
  }

  /** Derives octave offsets; the particle stream is drawn per rebuild. */
  private applySeedStreams(): void {
    const streams = deriveStreams(this.seedValue);
    streams.octaveOffsets.forEach(([x, y], i) => {
      this.octaveOffsets[i * 2] = x;
      this.octaveOffsets[i * 2 + 1] = y;
    });
  }

  private buildState(): Float32Array {
    // Fresh stream each rebuild: same seed -> same strand state.
    const streams = deriveStreams(this.seedValue);
    const p = this.params;
    return buildInitialState(
      p.particleCount,
      this.cssW / this.cssH,
      {
        lifetime: p.lifetime,
        lifetimeJitter: p.lifetimeJitter,
        stagger: p.stagger,
        emission: p.emission,
        plumeCount: p.plumeCount,
        spread: p.spread,
        halo: p.halo,
        zoom: p.zoom,
      },
      streams.particles,
    );
  }

  /** Steps at which the current plot settles — see plotTotalSteps. */
  private totalSteps(): number {
    const p = this.params;
    return plotTotalSteps(p.lifetime, p.lifetimeJitter, p.stagger);
  }

  /**
   * Start a fresh plot of the current seed + params from step 0: clear the
   * permanent ink, rebuild the strand state, un-settle, restart the loop.
   * Used by reseed, replay, reinit-class changes, resize and context restore.
   */
  private restartDrawing(): void {
    this.renderer.clearTrail(this.bg());
    this.sim.reset(this.buildState(), 0);
    this.settledFlag = false;
    this.emit("drawing", undefined);
    this.syncLoop();
    this.presentStillIfIdle();
  }

  private reseed(): void {
    this.applySeedStreams();
    this.restartDrawing();
  }

  private applyCanvasSize(): void {
    this.canvas.width = Math.max(1, Math.round(this.cssW * this.dpr));
    this.canvas.height = Math.max(1, Math.round(this.cssH * this.dpr));
    this.renderer.setSize(this.canvas.width, this.canvas.height, this.bg());
    if (this.exporting) {
      // The export owns the sim right now; re-plot after it settles.
      this.pendingReinit = true;
      return;
    }
    // Ink is permanent and the trail was just reallocated blank — the only
    // way to refill it at the new resolution is to re-plot from step 0.
    this.restartDrawing();
  }

  private loopActive(): boolean {
    return (
      this.userRunning &&
      !this.settledFlag &&
      !this.contextLost &&
      !this.exporting &&
      !this.destroyed
    );
  }

  private syncLoop(): void {
    this.loop.setActive(this.loopActive());
  }

  private tick(steps: number): void {
    if (this.destroyed || this.contextLost) return;
    if (steps > 0 && !this.settledFlag) {
      const total = this.totalSteps();
      const su = this.simUniforms();
      const du = this.stepUniforms(this.dpr);
      for (let i = 0; i < steps && this.sim.stepCount < total; i++) {
        this.sim.step(su);
        this.renderer.drawStep(this.sim.currentVao, this.sim.count, du);
      }
      if (this.sim.stepCount >= total) this.settle();
    }
    this.presentOnce();
    if (this.frames === 1) this.glErrorSweep("first frame");
  }

  /** The last strand finished: hold the frame, stop consuming GPU. */
  private settle(): void {
    this.settledFlag = true;
    this.syncLoop(); // loopActive() is false now — the loop stops entirely
    this.emit("settled", undefined);
  }

  private presentOnce(): void {
    this.renderer.present(null, this.params.exposure, this.sim.stepCount);
  }

  /** Present the current trail as-is, without advancing stepCount. Ink is
   *  permanent, so the trail already holds every deposit — re-running the
   *  dot pass here would double-deposit live strand heads and break the
   *  export replay's bit-exactness. */
  private presentStill(): void {
    this.presentOnce();
  }

  /** A paused OR settled engine has no live loop — mutations must still
   *  land on screen as a still frame. */
  private presentStillIfIdle(): void {
    if (this.contextLost || this.destroyed) return;
    if (this.userRunning && !this.settledFlag) return; // the loop presents
    if (this.exporting) {
      // The export owns the GL state right now; present when it settles.
      this.pendingPresent = true;
      return;
    }
    this.presentStill();
  }

  private onFrame(frameMs: number): void {
    this.frames += 1;
    this.fpsAccumMs += frameMs;
    this.fpsFrames += 1;
    if (this.fpsFrames >= FPS_EMIT_FRAMES) {
      const avg = this.fpsAccumMs / this.fpsFrames;
      this.fpsAccumMs = 0;
      this.fpsFrames = 0;
      this.emit("fps", { fps: 1000 / avg, frameMs: avg });
    }
    if (!this.debugLoop && !this.tuned && this.frames > TUNE_WARMUP_FRAMES) {
      this.tuneAccumMs += frameMs;
      if (this.frames >= TUNE_DECIDE_FRAME) {
        this.tuned = true;
        const avg = this.tuneAccumMs / (this.frames - TUNE_WARMUP_FRAMES);
        // dpr is ours to manage; particleCount is the user's — the UI sees
        // the fps events and decides.
        if (avg > TUNE_FRAME_MS && this.dpr > 1) {
          this.dprCap = Math.max(1, this.dpr / 2);
          this.dpr = this.dprCap;
          this.applyCanvasSize();
        }
      }
    }
  }

  /** Cached triple, re-derived only when palette/backgroundShade change. */
  private bg(): Rgb {
    const p = this.params;
    if (this.bgPalette !== p.palette || this.bgShade !== p.backgroundShade) {
      const tint = hexToLinear(PALETTES[p.palette].bgTint);
      const max = Math.max(tint[0], tint[1], tint[2], 1e-6);
      const s = p.backgroundShade / max;
      this.bgCache[0] = tint[0] * s;
      this.bgCache[1] = tint[1] * s;
      this.bgCache[2] = tint[2] * s;
      this.bgPalette = p.palette;
      this.bgShade = p.backgroundShade;
    }
    return this.bgCache;
  }

  private simUniforms(): SimUniforms {
    const p = this.params;
    const windRad = (p.windAngle * Math.PI) / 180;
    const u = this.simU;
    u.speed = p.speed;
    u.wind[0] = Math.cos(windRad) * p.windStrength * WIND_SPEED;
    u.wind[1] = Math.sin(windRad) * p.windStrength * WIND_SPEED;
    u.vortex = p.vortex;
    u.attract = p.attract;
    u.aspect = this.cssW / this.cssH;
    u.flowScale = p.flowScale;
    u.octaves = p.octaves;
    u.roughness = p.roughness;
    u.flowStrength = p.flowStrength;
    u.evolution = p.evolution;
    // u.octaveOffsets stays aliased to this.octaveOffsets (updated in place).
    return u;
  }

  private stepUniforms(resScale: number): StepUniforms {
    const p = this.params;
    if (!this.stopsCache || this.stopsCache.id !== p.palette) {
      const { stops, count } = paletteStopsLinear(PALETTES[p.palette]);
      this.stopsCache = { id: p.palette, stops, count };
    }
    const u = this.stepU;
    u.bg = this.bg();
    u.aspect = this.cssW / this.cssH;
    u.stops = this.stopsCache.stops;
    u.stopCount = this.stopsCache.count;
    u.dotSize = p.dotSize;
    u.resScale = resScale;
    u.zoom = p.zoom;
    u.sizeJitter = p.sizeJitter;
    u.dotOpacity = p.dotOpacity;
    u.glow = p.glow;
    u.symmetry = p.symmetry;
    return u;
  }

  private async runExport(
    scale: ExportScale,
    onProgress?: (t: number) => void,
  ): Promise<Blob> {
    if (this.destroyed) throw new EngineError("context-lost", "engine destroyed");
    if (this.contextLost) throw new EngineError("context-lost");
    if (scale > this.maxExportScale()) {
      throw new EngineError(
        "export-too-large",
        `scale ${scale}x exceeds maxExportScale() = ${this.maxExportScale()}`,
      );
    }
    this.exporting = true;
    this.syncLoop();
    const epoch = this.lossEpoch;
    const host: ExportHost = {
      gl: this.ctx.gl,
      sim: this.sim,
      programs: this.programs,
      preferFloatTrails: this.renderer.usesFloatTrail,
      maxPointSize: this.ctx.maxPointSize,
      cssWidth: this.cssW,
      cssHeight: this.cssH,
      maxTextureSize: this.ctx.maxTextureSize,
      totalSteps: this.totalSteps(),
      // Epoch check catches a context that was lost AND restored between
      // chunk boundaries — the captured GL objects died with the old context
      // even though isContextLost() reads false again.
      aborted: () => this.destroyed || this.lossEpoch !== epoch,
      buildState: () => this.buildState(),
      bg: () => this.bg(),
      exposure: () => this.params.exposure,
      // Copies, not the live mutate-in-place objects: nothing that happens
      // during or after the export may bleed into the captured replay.
      simUniforms: () => {
        const u = this.simUniforms();
        return {
          ...u,
          wind: [u.wind[0], u.wind[1]] as [number, number],
          octaveOffsets: u.octaveOffsets.slice(),
        };
      },
      stepUniforms: (rs) => {
        const u = this.stepUniforms(rs);
        return { ...u, bg: [u.bg[0], u.bg[1], u.bg[2]] as Rgb };
      },
    };
    try {
      return await exportPng(host, scale, onProgress);
    } finally {
      this.exporting = false;
      this.applyPendingMutations();
      this.syncLoop();
    }
  }

  /** Reinit work queued during an export lands here, after state restore. */
  private applyPendingMutations(): void {
    const reseed = this.pendingReseed;
    const reinit = this.pendingReinit;
    const present = this.pendingPresent;
    this.pendingReseed = false;
    this.pendingReinit = false;
    this.pendingPresent = false;
    if (this.destroyed || this.contextLost) return;
    if (reseed) {
      // buildState() reads the current params, so this covers reinit too.
      this.reseed();
    } else if (reinit) {
      this.restartDrawing();
    } else if (present) {
      // A live-key change or resize landed mid-export while idle.
      this.presentStillIfIdle();
    }
  }

  private handleContextLost(): void {
    this.contextLost = true;
    this.lossEpoch += 1;
    this.syncLoop();
    this.emit("contextlost", undefined);
  }

  private handleContextRestored(): void {
    if (this.destroyed) return;
    try {
      // All GPU objects died with the context: rebuild everything and
      // re-plot from the seed at step 0 (canonical time is step count).
      this.contextLost = false;
      this.initGpu();
      this.glErrorSweep("context restore");
      this.emit("contextrestored", undefined);
      // restartDrawing un-settles, emits "drawing", restarts the loop and —
      // for a paused engine that gets no tick — presents a still frame.
      this.restartDrawing();
    } catch (e) {
      this.contextLost = true;
      this.emit(
        "error",
        e instanceof EngineError
          ? e
          : new EngineError("context-lost", String(e)),
      );
    }
  }

  /** Cheap one-shot diagnostics — GPU bugs surface here, not as black canvas. */
  private glErrorSweep(tag: string): void {
    const gl = this.ctx.gl;
    for (let i = 0; i < 8; i++) {
      const err = gl.getError();
      if (err === gl.NO_ERROR) break;
      console.warn(`[eigenvisuals] GL error after ${tag}: 0x${err.toString(16)}`);
    }
  }
}
